import React, { createContext, useState, useEffect, useRef } from 'react';
import { useQueryParam, NumberParam, StringParam, ArrayParam } from 'use-query-params';
import {
  InterpolatedData,
  ContextData,
  BookmarkedFilter,
  Job,
  GetJobsResponse,
  SmartFilter,
  BookmarkedSearch,
  UserInfo,
} from './types';
import { UserData, UserPrivileges } from '../types/current_user.types';
import { parseQueryParamsForApi } from './utilities/job_search.utilities';
import {
  VIEW_JOBS_MEMBERS_ONLY,
  MANAGE_ARTICLES,
  VIEW_RECRUITER_TOOLS,
} from '../utilities/constants/user_privileges.constants';
import { getNonFeaturedJobs, getFeaturedJobs } from './services/job_search.service';
import { getUserData, getUserPrivileges } from '../services/current_user/current_user.service';
import { createSessionStorageObject } from './utilities/job_search.utilities';
import { GENERIC_ERROR_MESSAGE } from '../components/react/alert_popup/constants';

interface Props extends InterpolatedData {
  children: React.ReactNode;
}

const JobSearchContext = createContext<ContextData>(null);
// e.g. { text: 'COVID-19', value: 'covid OR covid-19 OR coronavirus', }
const smartFilters: SmartFilter[] | [] = [];
const defaultUserInfo = {
  userId: null,
  email: null,
  hasPrivateProfile: null,
  isSignedIn: false,
  membership: null,
  viewExclusiveJobs: false,
  hasPostedJobs: false,
  isAdmin: false,
};

const JobSearchContextProvider = ({ children, ...props }: Props) => {
  const [pageNumber = 1, setPageNumber] = useQueryParam('page[number]', NumberParam);
  const [locations, setLocations] = useQueryParam('filter[locations][]', ArrayParam);
  const [jobLevels, setJobLevels] = useQueryParam('filter[career_levels][]', ArrayParam);
  const [newsTopics, setNewsTopics] = useQueryParam('filter[news_topics][]', ArrayParam);
  const [isRemote = 'false', setIsRemote] = useQueryParam('filter[is_remote]', StringParam);
  const [hasSalaryData = 'false', setHasSalaryData] = useQueryParam(
    'filter[has_salary_data]',
    StringParam
  );
  const [stages, setStages] = useQueryParam('filter[stage][]', ArrayParam);
  const [type = '', setType] = useQueryParam('filter[job_types][]', StringParam);
  const [orgNames, setOrgNames] = useQueryParam('filter[organization_names][]', ArrayParam);
  const [field = '_score', setField] = useQueryParam('sorting[field]', StringParam);
  const [order = 'desc', setOrder] = useQueryParam('sorting[order]', StringParam);
  const [keyword, setKeyword] = useQueryParam('filter[keywords][]', StringParam);
  const [keywordUserInput = '', setKeywordUserInput] = useState<string>(keyword);
  const [userInfo, setUserInfo] = useState<UserInfo>(defaultUserInfo);
  const [newsTopicAggregations, setNewsTopicAggregations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [maxPages, setMaxPages] = useState(1);
  const [publicationInterval = '', setPublicationInterval] = useQueryParam(
    'filter[published_at_interval][]',
    StringParam
  );
  const [positionTypes, setPositionTypes] = useQueryParam('filter[contract_lengths][]', ArrayParam);
  const [extended, setExtended] = useState<boolean>(
    Boolean(orgNames?.length) || Boolean(stages?.length) || Boolean(type)
  );
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);
  const [nonFeaturedJobResults, setNonFeaturedJobResults] = useState<Job[]>([]);
  const [showAlerts, setShowAlerts] = useState<boolean>(false);
  const [searchSelected, setSearchSelected] = useState<BookmarkedSearch[] | []>([]);
  const [userRequested, setUserRequested] = useState<boolean>(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState<boolean>(false);
  const [alertData, setAlertData] = useState(null);

  const scrollToElement = useRef(null);
  const keywordsRef = useRef(null);

  const applySavedSearch = (filter: BookmarkedFilter) => {
    setIsRemote(filter?.is_remote);
    setKeywordUserInput(filter?.keywords?.[0] || '');
    setKeyword(filter?.keywords?.[0]);
    setPublicationInterval(filter?.published_at_interval?.[0]);
    setType(filter?.job_types?.[0]);

    // Set multi-select filters to empty arrays
    setLocations(filter?.locations || []);
    setJobLevels(filter?.career_levels || []);
    setNewsTopics(filter?.news_topics || []);
    setPositionTypes(filter?.contract_lengths || []);
    setOrgNames(filter?.organization_names || []);
    setStages(filter?.stage || []);

    // If saved search contains either active Organization, Job Stage or Job Type filters,
    // expand the More filters Collapsable section
    setExtended(
      Boolean(filter?.job_types) || Boolean(filter?.organization_names) || Boolean(filter?.stage)
    );
    setHasSalaryData(filter?.has_salary_data);
  };

  const queryParams = parseQueryParamsForApi({
    keyword: keywordUserInput.trim(),
    publicationInterval,
    type,
    isRemote,
    hasSalaryData,
    locations,
    jobLevels,
    newsTopics,
    positionTypes,
    orgNames,
    stages,
  });

  // Request user data
  useEffect(() => {
    setIsLoading(true);

    Promise.all([getUserData(), getUserPrivileges()])
      .then((responses: [UserData, UserPrivileges]) => {
        const [userData, privileges] = responses;

        setUserInfo({
          userId: userData?.id,
          email: userData?.primary_email_address?.email,
          hasPrivateProfile: !userData?.is_public,
          isSignedIn: true,
          membership: userData?.package_combination_name,
          hasPostedJobs: userData?.has_posted_jobs,
          viewExclusiveJobs: privileges?.granted.includes(VIEW_JOBS_MEMBERS_ONLY),
          isAdmin: privileges?.granted.includes(MANAGE_ARTICLES),
          viewRecruiterTools: privileges?.granted.includes(VIEW_RECRUITER_TOOLS),
        });
      })
      .catch(() => null)
      .finally(() => {
        setIsLoading(false);
        setUserRequested(true);
      });
  }, []);

  // Update search results when filter values change
  useEffect(() => {
    // Wait for the user data request
    if (!userRequested) return;

    setIsLoading(true);
    const { isSignedIn, membership } = userInfo;

    if (!isSignedIn) {
      delete queryParams.has_salary_data;
      setHasSalaryData(undefined);
    }

    // Make sure input keyword value is included in the search query.
    setKeyword(keywordUserInput.trim().length ? keywordUserInput : undefined);

    Promise.all([
      getNonFeaturedJobs(isSignedIn, queryParams, { field, order }, pageNumber, membership),
      getFeaturedJobs(isSignedIn, queryParams, { field, order }, pageNumber, membership),
    ])
      .then((responses: [GetJobsResponse, GetJobsResponse]) => {
        const [jobs, featuredJobsResponse] = responses;
        if (!newsTopicAggregations) setNewsTopicAggregations(jobs?.aggregations?.news_topics);

        setFeaturedJobs(featuredJobsResponse.data);
        setNonFeaturedJobResults(jobs.data);
        setMaxPages(jobs.page.pages);
        setTotal(jobs.total);
        setSuggestions(jobs.suggestions);

        const item = createSessionStorageObject(
          jobs,
          { filter: { ...queryParams, is_featured: false } },
          window.location.href
        );

        if (window.sessionStorage) window.sessionStorage.setItem('searchQuery', item);
      })
      .catch(() => {
        setAlertData({
          type: 'error',
          message: GENERIC_ERROR_MESSAGE,
        });
      })
      .finally(() => setIsLoading(false));
  }, [
    keyword,
    isRemote,
    locations,
    jobLevels,
    publicationInterval,
    newsTopics,
    positionTypes,
    orgNames,
    stages,
    type,
    pageNumber,
    field,
    order,
    hasSalaryData,
    userRequested,
  ]);

  const returnToValue = `${window.location.pathname}${encodeURIComponent(window.location.search)}`;

  const contextValue = {
    ...props,
    userInfo,
    pageNumber,
    setPageNumber,
    locations,
    setLocations,
    jobLevels,
    setJobLevels,
    newsTopics,
    setNewsTopics,
    isRemote,
    setIsRemote,
    hasSalaryData,
    setHasSalaryData,
    publicationInterval,
    setPublicationInterval,
    positionTypes,
    setPositionTypes,
    stages,
    setStages,
    type,
    setType,
    orgNames,
    setOrgNames,
    field,
    setField,
    setOrder,
    setKeyword,
    keywordUserInput,
    setKeywordUserInput,
    applySavedSearch,
    extended,
    setExtended,
    queryParams,
    featuredJobs,
    setFeaturedJobs,
    nonFeaturedJobResults,
    setNonFeaturedJobResults,
    isLoading,
    newsTopicAggregations,
    suggestions,
    total,
    maxPages,
    smartFilters,
    showAlerts,
    setShowAlerts,
    searchSelected,
    setSearchSelected,
    returnToValue,
    isMobileFiltersOpen,
    setIsMobileFiltersOpen,
    scrollToElement,
    keywordsRef,
    alertData,
    setAlertData,
  };

  return <JobSearchContext.Provider value={contextValue}>{children}</JobSearchContext.Provider>;
};

export { JobSearchContext, JobSearchContextProvider };
